import { AwaitContainer, ScrollArea } from '@ibsys/kit';
import { FC, Fragment, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type EditablePhraseType, useConversation } from '@/entities/conversations';
import { useTimeDisplayFormat } from '@/entities/settings';
import { useDialogSessionOptional } from '@/features/DialogSession';
import { getRoleSide, type SpeakerSide } from '../speaker';
import { useTranscriptData } from '../useTranscriptData';
import {
  countInterruptionsByPhrase,
  findGapIndex,
  getGapInterval,
  getRecordingDurationMs,
  mapPhrasesById,
  selectEditableRecords,
} from './records';
import { TranscriptAddRecordGap } from './TranscriptAddRecordGap';
import { TranscriptEditorContext, type TranscriptEditorContextValue } from './TranscriptEditorContext';
import { TranscriptNewRecordForm } from './TranscriptNewRecordForm';
import { TranscriptRecordEditForm } from './TranscriptRecordEditForm';
import { TranscriptRecordItem } from './TranscriptRecordItem';
import type { TranscriptEditorState } from './useTranscriptEditorState';

type Props = {
  dialogId: number;
  editor: TranscriptEditorState;
};

/**
 * Лента режима редактирования расшифровки: каждая реплика, перебивание и пауза — отдельной строкой,
 * новая запись добавляется в промежуток между строками. Правится анонимизированный текст: при показе
 * без анонимизации в режим не войти.
 */
export const TranscriptEditor: FC<Props> = ({ dialogId, editor }) => {
  const { t } = useTranslation(['dialogs']);
  const timeFormat = useTimeDisplayFormat('transcript');
  const { data, isLoading, transcriptContainerRef } = useTranscriptData(dialogId, true);
  const { data: conversation } = useConversation(dialogId);
  const playerDurationSec = useDialogSessionOptional()?.duration;
  const { openedForm, isDirty, phrasePick, openForm, closeForm } = editor;

  const records = useMemo(() => selectEditableRecords(data), [data]);
  const phrasesById = useMemo(() => mapPhrasesById(records), [records]);
  const interruptionCountByPhraseId = useMemo(() => countInterruptionsByPhrase(records), [records]);
  const personSideById = useMemo(
    () =>
      new Map<number, SpeakerSide>(
        (conversation?.persons ?? []).map((person) => [person.id, getRoleSide(person.role)]),
      ),
    [conversation?.persons],
  );
  const recordingDurationMs = getRecordingDurationMs(records, playerDurationSec);

  const contextValue = useMemo<TranscriptEditorContextValue>(
    () => ({
      ...editor,
      conversationId: dialogId,
      phrasesById,
      interruptionCountByPhraseId,
      personSideById,
      timeFormat,
      recordingDurationMs,
    }),
    [editor, dialogId, phrasesById, interruptionCountByPhraseId, personSideById, timeFormat, recordingDurationMs],
  );

  // Запись, открытая на правку, пропала из расшифровки — править больше нечего
  const editedRecordId = openedForm?.mode === 'edit' ? openedForm.recordId : null;
  const hasEditedRecord = editedRecordId === null || records.some((record) => record.id === editedRecordId);
  useEffect(() => {
    if (!isLoading && !hasEditedRecord) closeForm();
  }, [isLoading, hasEditedRecord, closeForm]);

  const createForm = openedForm?.mode === 'create' ? openedForm : null;
  const createGapIndex = createForm ? findGapIndex(records, createForm.afterRecordId) : null;

  // Промежуток перед записью с индексом `index`; под последней записью — `index` = числу записей
  const renderGap = (index: number) => {
    if (createForm && index === createGapIndex) {
      return (
        <div className="py-3">
          <TranscriptNewRecordForm key={`${createForm.afterRecordId}-${createForm.type}`} form={createForm} />
        </div>
      );
    }
    const handleAdd = (type: EditablePhraseType) =>
      openForm({
        mode: 'create',
        type,
        afterRecordId: records[index - 1]?.id ?? null,
        ...getGapInterval(records, index, recordingDurationMs),
      });
    return <TranscriptAddRecordGap isAlwaysVisible={records.length === 0} disabled={isDirty} onAdd={handleAdd} />;
  };

  return (
    <TranscriptEditorContext.Provider value={contextValue}>
      <div className="flex h-full min-h-0 flex-col">
        {phrasePick && (
          <p className="px-3 pb-2 text-xs text-muted-foreground">{t('dialogs:transcriptEdit.pickHint')}</p>
        )}
        <ScrollArea className="flex-1 min-h-0 border-t">
          <AwaitContainer isLoading={isLoading}>
            <div ref={transcriptContainerRef} className="flex flex-col p-3">
              {records.map((record, index) => (
                <Fragment key={record.id}>
                  {renderGap(index)}
                  {record.id === editedRecordId ? (
                    <TranscriptRecordEditForm record={record} />
                  ) : (
                    <TranscriptRecordItem record={record} />
                  )}
                </Fragment>
              ))}
              {renderGap(records.length)}
              {records.length === 0 && (
                <p className="text-sm text-muted-foreground">{t('dialogs:transcriptEdit.empty')}</p>
              )}
            </div>
          </AwaitContainer>
        </ScrollArea>
      </div>
    </TranscriptEditorContext.Provider>
  );
};
